/**
 * useWorkflowPage — the single hook powering the Generic Workflow Page.
 *
 * Loads surface (definition+ui+statusSet+outcomes), queue page, and agent
 * permissions in one shot. Queue navigation (tabs, filters, pagination) is
 * local state; data fetches happen on demand.
 *
 * Owner requirements satisfied here:
 *  #2 Queue holds identity (orderRef) not order data. Order records load on expand.
 *  #4 No Google Sheet reads here — all from D1 via QueryService.
 *  #6 Permissions come from PermissionEngine (/permissions endpoint) not role names.
 *  #8 Pagination always enforced (limit=50, offset increments).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { engineApi } from '../../sdk/engine.api'
import type {
  WorkflowId, WorkflowSurface, QueueEntry,
  WorkflowPermissions, EnginePhase, Permission, UiSortSpec,
} from '../../sdk/engine.api'
import { getShop } from '../../sdk/shop'

export type QueueFilters = {
  phase?:    EnginePhase[]
  status?:   string
  agent?:    string
  meta?:     Record<string, string>
  limit:     number
  offset:    number
}

export type WorkflowPageState = {
  surface:      WorkflowSurface | null
  permissions:  WorkflowPermissions | null
  queue:        { items: QueueEntry[]; total: number; limit: number; offset: number } | null
  filters:      QueueFilters
  loading:      boolean
  queueLoading: boolean
  error:        string | null
  shop:         string
  workflowId:   WorkflowId
}

export type WorkflowPageActions = {
  refresh:          () => void
  refreshQueue:     () => void
  setFilter:        (patch: Partial<Omit<QueueFilters, 'offset'>>) => void
  nextPage:         () => void
  prevPage:         () => void
  can:              (p: Permission) => boolean
}

export function useWorkflowPage(workflowId: WorkflowId, defaultAgentId?: string) {
  const shop = getShop() ?? ''

  const [surface,     setSurface]     = useState<WorkflowSurface | null>(null)
  const [permissions, setPermissions] = useState<WorkflowPermissions | null>(null)
  const defaultSortRef = useRef<UiSortSpec[] | undefined>(undefined)
  const [queue,       setQueue]       = useState<WorkflowPageState['queue']>(null)
  const [filters,     setFilters]     = useState<QueueFilters>(() => ({
    limit: 50, offset: 0,
    agent: defaultAgentId || undefined,
  }))
  const [loading,     setLoading]     = useState(true)
  const [queueLoading,setQueueLoading]= useState(false)
  const [error,       setError]       = useState<string | null>(null)

  const loadSurface = useCallback(async () => {
    if (!shop || !workflowId) return
    setLoading(true)
    setError(null)
    try {
      const [surf, perms] = await Promise.all([
        engineApi.surface(shop, workflowId),
        engineApi.permissions(shop, workflowId),
      ])
      setSurface(surf)
      setPermissions(perms)
      defaultSortRef.current = surf.ui.defaultSort
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load workflow')
    } finally {
      setLoading(false)
    }
  }, [shop, workflowId])

  const loadQueue = useCallback(async (f: QueueFilters) => {
    if (!shop || !workflowId) return
    setQueueLoading(true)
    try {
      const page = await engineApi.queue(shop, workflowId, {
        phase:  f.phase,
        status: f.status,
        agent:  f.agent,
        meta:   f.meta,
        limit:  f.limit,
        offset: f.offset,
        sort:   defaultSortRef.current,
      })
      setQueue(page)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load queue')
    } finally {
      setQueueLoading(false)
    }
  }, [shop, workflowId])

  useEffect(() => { loadSurface() }, [loadSurface])
  useEffect(() => { loadQueue(filters) }, [loadQueue, filters])

  const setFilter = useCallback((patch: Partial<Omit<QueueFilters, 'offset'>>) => {
    setFilters(prev => ({ ...prev, ...patch, offset: 0 }))
  }, [])

  const nextPage = useCallback(() => {
    setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }))
  }, [])

  const prevPage = useCallback(() => {
    setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))
  }, [])

  const grantedSet = new Set(permissions?.granted ?? [])
  const can = (p: Permission) => Boolean(permissions?.bootstrap) || grantedSet.has(p)

  return {
    state: { surface, permissions, queue, filters, loading, queueLoading, error, shop, workflowId } as WorkflowPageState,
    actions: {
      refresh:      loadSurface,
      refreshQueue: () => loadQueue(filters),
      setFilter,
      nextPage,
      prevPage,
      can,
    } as WorkflowPageActions,
  }
}
